/**
 * Kapitel 3.2 (Screen 7): „Eigenes Passwort festlegen" nach der Erstanmeldung.
 *
 * Erreichbar nur mit `user.passwordIsInitial === true` (Anmeldung mit dem
 * Filialpasswort des Haupt-Standorts oder einem Startpasswort, das Chef bzw.
 * Filialleitung vergeben hat). ProtectedRoute leitet in diesem Zustand jede
 * andere Seite hierher um, und der Server lässt bis dahin nur /api/auth/* zu.
 * Nach dem Speichern ist das Filialpasswort für dieses Konto wertlos.
 */

import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth, type AuthUser } from '../store/auth';
import { apiFetch, ApiError } from '../lib/api';
import { hapticError, hapticSuccess } from '../lib/haptics';

export default function SetInitialPasswordPage() {
  const token = useAuth((s) => s.token);
  const user = useAuth((s) => s.user);
  const setUser = useAuth((s) => s.setUser);
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState('');
  const [newPassword2, setNewPassword2] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!token || !user) return <Navigate to="/login" replace />;
  if (!user.passwordIsInitial) return <Navigate to="/" replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (newPassword.length < 8) {
      setError('Mindestens 8 Zeichen.');
      hapticError();
      return;
    }
    if (newPassword !== newPassword2) {
      setError('Die beiden Passwörter stimmen nicht überein.');
      hapticError();
      return;
    }
    setBusy(true);
    try {
      const data = await apiFetch<{ user: AuthUser; recoveryCode?: string }>('/api/auth/set-initial-password', {
        method: 'POST',
        token,
        body: { newPassword },
      });
      hapticSuccess();
      setUser(data.user);
      // Phase L: Chefs bekommen hier ggf. ihren Wiederherstellungs-Code (einmalig).
      if (data.recoveryCode) useAuth.getState().setPendingRecoveryCode(data.recoveryCode);
      navigate('/', { replace: true });
    } catch (err) {
      hapticError();
      setError(err instanceof ApiError ? err.message : 'Passwort konnte nicht gespeichert werden.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="max-w-sm mx-auto mt-12">
      <h2 className="text-2xl font-bold text-center mb-2">Eigenes Passwort festlegen</h2>
      <p className="text-sm text-slate-400 text-center mb-6">
        Hallo {user.name.split(' ')[0]}! Du hast dich mit einem Start- bzw. Filialpasswort angemeldet.
        Lege jetzt dein persönliches Passwort fest — ab dann gilt nur noch dieses.
      </p>
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label htmlFor="new-password" className="block text-sm text-slate-300 mb-1">
            Neues Passwort
          </label>
          <input
            id="new-password"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 focus:outline-none focus:border-brand"
          />
          <p className="text-[11px] text-slate-500 mt-1">
            Mindestens 8 Zeichen. Nicht das Filialpasswort — das kennen alle am Standort.
          </p>
        </div>
        <div>
          <label htmlFor="new-password-2" className="block text-sm text-slate-300 mb-1">
            Neues Passwort (Wiederholung)
          </label>
          <input
            id="new-password-2"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={newPassword2}
            onChange={(e) => setNewPassword2(e.target.value)}
            className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 focus:outline-none focus:border-brand"
          />
        </div>
        {error && (
          <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded px-3 py-2">{error}</p>
        )}
        <button
          type="submit"
          disabled={busy}
          className="w-full bg-brand hover:bg-brand-dark transition rounded py-2 font-medium disabled:opacity-50"
        >
          {busy ? 'Speichern…' : 'Passwort speichern'}
        </button>
      </form>
    </div>
  );
}
